import React, { ErrorInfo } from 'react';
import { createRoot } from 'react-dom/client';
import * as Sentry from '@sentry/react';
import App from './App.tsx';
import './index.css';
import './styles/prose.css';
import './styles/highlight.css';

// Import i18n (must be imported before any component that uses it)
import './i18n';
import { logError } from '@/utils/errorHandler';

// Initialize Sentry for error tracking and performance monitoring
const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN;

if (SENTRY_DSN) {
  Sentry.init({
    dsn: SENTRY_DSN,
    environment: import.meta.env.VITE_SENTRY_ENVIRONMENT || import.meta.env.MODE,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
        // Enable capturing request and response headers and bodies
        networkDetailAllowUrls: [
          // Capture all requests to your API
          /^https?:\/\/.*kernlogic\.com/,
          /^https?:\/\/localhost/,
          /^https?:\/\/127\.0\.0\.1/,
          // Add other domains you want to capture
          /^https?:\/\/.*\.amazonaws\.com/,
        ],
        networkCaptureBodies: true,
        networkRequestHeaders: [
          'Accept',
          'Accept-Language',
          'Authorization',
          'Content-Type',
          'User-Agent',
          'X-Requested-With',
          'X-CSRFToken',
          // Add any custom headers you use
        ],
        networkResponseHeaders: [
          'Content-Type',
          'Content-Length',
          'Cache-Control',
          'Set-Cookie',
          'X-Request-ID',
          // Add any custom response headers you want to capture
        ],
      }),
    ],
    // Performance Monitoring
    tracesSampleRate: parseFloat(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE || '0.1'),
    // Session Replay
    replaysSessionSampleRate: parseFloat(
      import.meta.env.VITE_SENTRY_REPLAYS_SESSION_SAMPLE_RATE || '0.02'
    ),
    replaysOnErrorSampleRate: parseFloat(
      import.meta.env.VITE_SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE || '1.0'
    ),

    // Set sample rate for profiling
    profilesSampleRate: parseFloat(import.meta.env.VITE_SENTRY_PROFILES_SAMPLE_RATE || '0.1'),

    // Setting this option to true will send default PII data to Sentry.
    sendDefaultPii: import.meta.env.VITE_SENTRY_SEND_DEFAULT_PII === 'true',

    // Enable in production and when explicitly enabled via env var
    enabled:
      import.meta.env.MODE === 'production' || import.meta.env.VITE_SENTRY_ENABLED === 'true',
  });
}

class ErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean; error: Error | null; errorInfo: ErrorInfo | null }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    logError(error, {
      component: 'ErrorBoundary',
      action: 'componentDidCatch',
      metadata: {
        componentStack: errorInfo.componentStack,
        context: 'react_error_boundary',
      },
    });
    this.setState({ errorInfo });
  }

  render() {
    if (this.state.hasError) {
      return (
        <div
          style={{
            padding: '20px',
            backgroundColor: '#ffeeee',
            border: '1px solid #ff5555',
            borderRadius: '8px',
            margin: '20px',
            fontFamily: 'Arial, sans-serif',
          }}
        >
          <h1 style={{ color: '#cc0000' }}>Something went wrong</h1>
          <details style={{ whiteSpace: 'pre-wrap', marginTop: '10px' }}>
            <summary style={{ cursor: 'pointer', fontWeight: 'bold' }}>Show error details</summary>
            <p style={{ marginTop: '10px' }}>{this.state.error?.toString()}</p>
            <p style={{ marginTop: '10px' }}>Component Stack:</p>
            <pre
              style={{
                backgroundColor: '#f8f8f8',
                padding: '10px',
                overflow: 'auto',
              }}
            >
              {this.state.errorInfo?.componentStack}
            </pre>
          </details>
        </div>
      );
    }

    return this.props.children;
  }
}

// Ensure we only create root once
const container = document.getElementById('root');
if (!container) {
  throw new Error('Root element not found');
}

const root = createRoot(container);
root.render(
  <ErrorBoundary>
    <App />
  </ErrorBoundary>
);
